/**
 * run_batch() orchestration (005 T007/T008).
 *
 * Delegates per design×view to the EngineBinding.renderBatch (004, off-thread),
 * builds OutputRecords with buildRef() paths, and returns refs/print-files/
 * failures. Writing to disk/zip is the caller's job (export/bundle.ts via
 * PlatformIO) — this stays pure so it's testable without a platform.
 */
import type { BatchResult, DesignBatch, OutputRecord, PrintFileRef } from "@genz/platform";
import { getExportPrintFiles } from "../stores/exportPrintFiles.js";
import type { EngineBinding, RenderBatchJob, RenderSettings, ViewName, BatchProgress, EngineTraceEvent, WireTemplateVersion, MockupAssets, MockupSessionRefused } from "@genz/engine-bind";
import { mockupSessionArgs, MOCKUP_SESSION_BUDGET } from "@genz/engine-bind";
import { PRIMARY_VIEW } from "../templates/views.js";
import { buildRef, dedupeSlugs, designFileBase } from "../export/refs.js";

export interface RunBatchContext {
  customer: string;
  product: string;
  /** Template name → a path segment so different templates export to different folders. */
  templateName?: string;
  phoneSlug: string;
  caseStyleSlug: string;
  ext: "jpg" | "png" | "webp";
  // `printDpi` lived here and reached nothing — the shipped print file is the customer's raw
  // upload, not a DPI-scaled render (AUD-3 LB-7 / D-10). Removed rather than left looking
  // wired; `render_settings.print_dpi` stays on the wire for WS-H2.
  /** Mockups export scheme (`<Product Type>/<Brand>/<Model>/[<Category>/]<View>/<DesignName>.<ext>`).
   *  These are HUMAN labels for the folder tree. When the funnel hasn't supplied them yet they
   *  are derived from the legacy context (product/phoneSlug/caseStyle) by the caller — the render
   *  never crashes on a missing field (buildRef falls back to `product`/`designSlug`). */
  productType?: string;
  brand?: string;
  model?: string;
  /** Canonical wire template version (from the cloud catalog/sync API). The engine
   * renders from its geometry — this is how the adapter is wired into the render path. */
  templateVersion: WireTemplateVersion;
  /** Optional encoded brand-logo bytes (spec 017 US6). When set AND a view has a
   * `brand_logo_placement`, the deterministic mockup composite merges the logo in. */
  brandLogo?: Uint8Array;
  /** Persisted brand-logo asset URL (the queue survives restarts, so bytes can't be
   * stored). The queue worker fetches this → `brandLogo` at render time. */
  brandLogoRef?: string;
  /** Spec 019: resolved baked mockup assets (shell/mask/generated bytes + profile). When
   * present, every item renders as a LAYERED MOCKUP via the engine compositor instead of
   * the classic warp-over-background path. Resolved from local storage by the caller. */
  mockup?: MockupAssets;
  /** Task B3: per-view baked assets, resolved by the caller (queue worker) up front so each
   * (design × view) item composites with ITS view's bake. A view absent here falls back to
   * `mockup`, then classic render. Values are shared references — bytes are never copied. */
  mockupByView?: Record<string, MockupAssets | null>;
  /**
   * Per view, the template's CASE PHOTO (encoded) — resolved by the caller from that view's
   * `base_case_image_ref` BEFORE the run starts: the hosted ref, else this device's local image
   * store. Mirrors `mockupByView`, because the ref is a per-view field.
   *
   * Used only by the CLASSIC (no-bake) path, which is exactly the path that used to export a
   * bare canvas. Resolved ONCE per run, not per item: it is the same product in every item, so
   * per-item resolution would re-read (and on web re-download) the same megabytes N times.
   */
  baseImageByView?: Record<string, Uint8Array | null>;
}

export interface RunBatchOptions {
  settings: RenderSettings;
  onProgress?: (p: BatchProgress) => void;
  onTrace?: (e: EngineTraceEvent) => void;
  /**
   * Cooperative cancellation (Pause/Cancel in the queue). Checked BEFORE each design, so a
   * paused/cancelled run stops after the design in flight instead of rendering the whole
   * batch to completion and only then having its status overwritten (the "Cancel does
   * nothing" bug). Returning a non-null reason aborts; whatever already rendered is kept
   * and reported via {@link BatchResult.aborted}.
   *
   * This is why the batch is submitted to the engine ONE DESIGN AT A TIME: `renderBatch` is
   * a single un-interruptible call, so the abort check has to live between calls. The
   * engine renders items serially anyway (`runBatchSerially`), so the only cost is one
   * extra postMessage per design on the worker-backed (web) binding.
   */
  shouldAbort?: () => string | null;
  /**
   * WS-H1: render ONLY the (design, view) items this returns true for. Two callers:
   *  - `QueueManager.retryFailed(jobId)` — re-run just the failed items (no duplicate output).
   *  - `QueueManager.resume(jobId)` — skip the items the pause checkpoint already published.
   * Absent = render everything (unchanged behavior). Filtering happens HERE, above the
   * engine, so the item boundary and the IPC frame stay exactly as WS-H2 will find them.
   */
  itemFilter?: (designSlug: string, view: ViewName) => boolean;
  /**
   * WS-O / D-36: ONE item just finished — its bytes exist and are about to go into the
   * accumulator. Called INSIDE the per-design loop, immediately after the output record is
   * built and (separately) after the print file renders, i.e. at the earliest instant the
   * bytes exist at all. AUD-8 probe B: without this the first surfacing of ANY byte is
   * `runBatch`'s return, ~7 minutes into a 500-design run.
   *
   * AWAITED. The writer is a bounded serial queue and awaiting it is what bounds memory: an
   * un-awaited sink lets the loop out-run the disk and re-creates the very heap this wave
   * exists to flatten. The cost is one IPC write per item against an 857 ms render — under 1 %.
   *
   * A THROW here must never fail the render. `runBatch` catches it, emits a `warn` trace
   * naming the item and the reason, and continues: the item is still in `result.outputs`, so
   * the end-of-run reconciliation in `QueueManager` still sees it and the Error log still
   * names it. Losing a write is a reportable export failure, not a lost render.
   */
  onItem?: (item: RunItem) => void | Promise<void>;
}

/**
 * One finished unit of a run, whatever produced it. `kind` is the discriminant the writer
 * branches on for the bundle root ("Mockups" vs "Transparent") and for the print-file
 * precedence rule; everything else it needs is already on the record.
 */
export type RunItem =
  | { kind: "mockup"; output: OutputRecord }
  | { kind: "print"; print: PrintFileRef & { bytes?: Uint8Array; templateId?: string } }
  // `source` is the converted file's own path; `relPath` is its `.png` output (AR-11).
  | { kind: "transparent"; source: string; relPath: string; bytes: Uint8Array };

let outCounter = 0;
function outId(): string {
  outCounter += 1;
  return `out-${Date.now().toString(36)}-${outCounter}`;
}

/** `BatchResult` plus the cooperative-abort marker (see {@link RunBatchOptions.shouldAbort}).
 *  Kept local to this module so the shared platform contract stays unchanged. */
export type RunBatchResult = BatchResult & { aborted?: string };

export async function runBatch(
  engine: EngineBinding,
  batch: DesignBatch,
  ctx: RunBatchContext,
  opts: RunBatchOptions,
): Promise<RunBatchResult> {
  const designsWithBytes = batch.designs.filter((d) => d.bytes && d.bytes.length > 0);
  const views = batch.views as ViewName[];

  // WS-H2: filled below, one entry per view that successfully opened a native render session.
  // Passed on every job so `renderMockupItem` sends only the design for that view.
  const sessionByView: Record<string, string> = {};

  const jobFor = (designs: typeof designsWithBytes, jobViews: ViewName[]): RenderBatchJob => ({
    templateVersion: ctx.templateVersion,
    designs: designs.map((d) => ({ design: d.bytes!, designSlug: d.designSlug })),
    views: jobViews,
    settings: opts.settings,
    brandLogo: ctx.brandLogo,
    baseImageByView: ctx.baseImageByView,
    mockup: ctx.mockup,
    mockupByView: ctx.mockupByView,
    sessionByView,
  });

  // WS-H1: filtered PLAN. A design contributes only the views the filter kept, and a design
  // with no surviving view is never submitted (an empty `views` array would render nothing and
  // still pay a full IPC round trip). Without a filter this is the full design × view grid,
  // i.e. today's behavior unchanged.
  const plan = designsWithBytes
    .map((d) => ({
      d,
      views: opts.itemFilter ? views.filter((v) => opts.itemFilter!(d.designSlug, v)) : views,
    }))
    .filter((p) => p.views.length > 0);

  // Render ONE DESIGN AT A TIME so Pause/Cancel can actually stop the run between designs
  // (see RunBatchOptions.shouldAbort). Progress is re-based onto the whole batch's totals so
  // the UI bar is identical to the old single-call behavior — and on a partial re-run those
  // totals are the FILTERED plan's, so the bar reads k/k rather than k/N.
  const total = plan.reduce((n, p) => n + p.views.length, 0);
  const result: { outputs: OutputRecord[]; failures: BatchResult["failures"] } =
    { outputs: [], failures: [] };
  let aborted: string | undefined;
  let done = 0;
  /** D-10: the RENDERED print files, one per design that rendered its back view. */
  const renderedPrintFiles: (PrintFileRef & { bytes?: Uint8Array; templateId?: string })[] = [];

  // WS-O / D-36: announce ONE finished item. NEVER throws — a sink that blows up is an export
  // problem to report, not a render to lose (see `RunBatchOptions.onItem`). Declared here so
  // both call sites inside the loop share one implementation and one warn sentence.
  const announce = async (item: RunItem, itemKey: string): Promise<void> => {
    if (!opts.onItem) return;
    try {
      await opts.onItem(item);
    } catch (e) {
      opts.onTrace?.({
        itemKey, phase: "render", startedAt: Date.now(), level: "warn",
        message: `${itemKey} rendered but was not written (${e instanceof Error ? e.message : String(e)}) — ` +
          `it is still in this run's results; see Settings → Observability → Error log`,
      });
    }
  };

  // Per-design lookup keyed by the (deduped) designSlug the engine echoes back: the HUMAN
  // design name (original filename base), deduped across the WHOLE batch — every file lands in
  // one model folder, so two files that pathSafe to the same base, from any source folder,
  // must not collide (dedupeSlugs → -2, -3, …).
  // WS-O: resolved BEFORE the loop (they only depend on the staged designs) so each output's
  // real export path exists at the instant its bytes do — the writer needs the path, and
  // deriving it a second time downstream is how two truths appear.
  // De-dup on `designFileBase` — the EXACT string buildRef writes — not on the raw base name.
  // Deduping the pre-strip name let two designs that only collided after buildRef's own strip
  // ("photo.png.png" / "photo.jpg.png" → both "photo") walk past the guard onto one file.
  const dedupedNames = dedupeSlugs(designsWithBytes.map((d) => designFileBase(baseName(d.filename))));
  const nameBySlug = new Map(designsWithBytes.map((d, i) => [d.designSlug, dedupedNames[i]]));

  // Shared parts so an output and its print derivative resolve 1:1 in the mockups tree.
  const mockupParts = (designSlug: string, view: ViewName, ext: string) => ({
    scheme: "mockups" as const,
    customer: ctx.customer,
    product: ctx.product,
    // Data-loss fix: without this segment two templates for the SAME product/brand/model
    // wrote byte-identical paths and a multi-template run overwrote its own output.
    templateName: ctx.templateName,
    phoneSlug: ctx.phoneSlug,
    caseStyleSlug: ctx.caseStyleSlug,
    designSlug,
    view,
    ext,
    productType: ctx.productType,
    brand: ctx.brand,
    model: ctx.model,
    designName: nameBySlug.get(designSlug) ?? designSlug,
  });

  // WS-H2 (AUD-3 LB-1): open ONE render session per view before the loop, so the ~13-17 MB of
  // baked assets cross the IPC once per (template, view) instead of once per item. Opened HERE
  // — above the per-design chunking that Pause/Cancel depends on (`shouldAbort`) — and closed in
  // a `finally`, so an abort, a throw and a clean finish all release them. A binding without the
  // session API (wasm) simply skips this and takes the per-item path. The loop reads from `plan`
  // (the FILTERED plan), so a retry of one failed item opens exactly one session.
  try {
    if (engine.openMockupSession && engine.composeInSession) {
      // WS-H2 / review-1 I-5(b): the FIRST budget refusal for this job. The native session
      // table is shared across concurrently running jobs AND across this job's own views, so
      // either can legitimately fill it; that is a capacity fact about the whole job, not news
      // about each view.
      // Collected here and reported ONCE below.
      let budgetRefusal: MockupSessionRefused | null = null;
      for (const view of new Set(plan.flatMap((p) => p.views))) {
        const m = ctx.mockupByView?.[view] ?? ctx.mockup ?? null;
        if (!m) continue;
        try {
          const id = await engine.openMockupSession(mockupSessionArgs(jobFor([], [view]), m, view));
          if (typeof id === "string" && id) {
            sessionByView[view] = id;
            opts.onTrace?.({
              itemKey: `session:${view}`, phase: "preparing", startedAt: Date.now(), level: "info",
              message: `render session opened for view "${view}" — baked assets cross the IPC once`,
            });
          } else if (id && typeof id === "object" && id.refused === MOCKUP_SESSION_BUDGET) {
            // No session for this view: the loop below renders those items per item, which is
            // the pre-WS-H2 path — correct, just slower. Views that DID get a session keep it.
            budgetRefusal ??= id;
          }
        } catch (e) {
          // A session is an optimisation. Losing one costs speed, never correctness.
          opts.onTrace?.({
            itemKey: `session:${view}`, phase: "preparing", startedAt: Date.now(), level: "warn",
            message: `could not open a render session for view "${view}" (${e instanceof Error ? e.message : String(e)}) — rendering per item`,
          });
        }
      }
      if (budgetRefusal) {
        // ONE warn for the JOB, stating only what the refusal payload actually knows: how much
        // of the budget is held and by how many sessions. It does NOT claim those sessions
        // belong to other jobs — review-2 N-3(a): `resident_bytes` is ~22 B/px per view, so a
        // single 4-view job above ~2 470² fills the 512 MiB budget with its OWN earlier views
        // and has the rest refused at concurrency 1; the old copy told that operator to lower a
        // knob already at its floor (the same class of defect as M-2). Concurrency is offered as
        // a possible cause and a remedy where it applies — `QueuePanel` goes up to 8, and two
        // concurrent 4-view jobs at 2000² already want ~704 MB.
        opts.onTrace?.({
          itemKey: "session:budget", phase: "preparing", startedAt: Date.now(), level: "warn",
          message:
            `render sessions refused for this job — the ${budgetRefusal.budgetMb} MiB session ` +
            `budget is already holding ${budgetRefusal.residentMb} MiB across ` +
            `${budgetRefusal.sessions} session(s) (this job's earlier views and/or other jobs ` +
            `running at the same time), and this job needed ${budgetRefusal.requestedMb} MiB ` +
            `more. Rendering it per item (correct, slower). If other jobs are running, lowering ` +
            `the Queue concurrency gives each job its sessions back; otherwise this bake is ` +
            `simply too large for more than ${budgetRefusal.sessions} view(s) at once.`,
        });
      }
    }
    for (const { d, views: designViews } of plan) {
      const reason = opts.shouldAbort?.();
      if (reason) {
        aborted = reason;
        break;
      }
      const chunk = await engine.renderBatch(
        jobFor([d], designViews),
        opts.onProgress ? (p) => opts.onProgress!({ ...p, completed: done + p.completed, total }) : undefined,
        opts.onTrace,
      );
      // WS-O / D-36: the output RECORD is built here, in the loop, instead of in a post-loop
      // map — so the bytes and their final export path exist in the same instant, and the
      // writer can put them on disk before the next design's engine call begins. The record
      // itself is byte-for-byte what the post-loop map produced.
      for (const o of chunk.outputs) {
        const record: OutputRecord = {
          id: outId(),
          batchId: batch.id,
          templateId: batch.templateId,
          designSlug: o.designSlug,
          view: o.view,
          format: o.format,
          // Extension MUST follow the ACTUAL bytes: the layered-mockup compositor always emits
          // PNG (o.format), while ctx.ext carries the workspace's requested mockup format — a
          // PNG byte stream saved as `<name>.webp`/`.jpg` won't open in Photos/Preview (the
          // "saved file doesn't open" bug). The classic path's o.format matches ctx.ext anyway.
          localRef: buildRef(mockupParts(o.designSlug, o.view, o.format || ctx.ext)),
          contentHash: o.contentHash,
          bytes: o.bytes,
        };
        result.outputs.push(record);
        await announce({ kind: "mockup", output: record }, `${o.designSlug}:${o.view}`);
      }
      result.failures.push(...chunk.failures);
      done += designViews.length;

      // WS-H2 / D-10: the print file is RENDERED at `render_settings.print_dpi` now, per design,
      // right where the design's bytes and the engine are both already in hand. Rendered inside
      // the loop (not after it) so a paused or cancelled run never pays for designs it skipped.
      //
      // WS-Y: …and not rendered at all while print files are off. Nothing downstream would
      // write these bytes (`export/runWriter.ts` skips a `print` item, `export/bundle.ts` drops
      // the refs), so asking for them would buy one native re-render per design — the most
      // expensive no-op in the run — plus one PNG per design held against the output-bytes
      // budget. The switch is read per design rather than hoisted, so a flip takes effect on
      // the next design instead of on the next app start.
      if (engine.renderPrintFile && getExportPrintFiles() && designViews.includes(PRIMARY_VIEW)) {
        try {
          const pf = await engine.renderPrintFile({
            templateVersion: ctx.templateVersion,
            design: d.bytes!,
            designSlug: d.designSlug,
            settings: opts.settings,
          });
          if (pf) {
            const ref = {
              designSlug: pf.designSlug,
              view: PRIMARY_VIEW,
              dpi: pf.dpi,
              localRef: "", // the real path is derived at export time by printFilesFromOutputs
              bytes: pf.bytes,
              templateId: batch.templateId,
            };
            renderedPrintFiles.push(ref);
            // WS-O / D-36: the print file is announced AFTER its design's mockup items and
            // only on a SUCCESSFUL render, so the writer never sees a half-made ref. The
            // announcement cannot throw (see `announce`), so a sink failure can never be
            // mistaken for the print-file failure the `catch` below reports.
            await announce({ kind: "print", print: ref }, `${d.designSlug}:print`);
          }
        } catch (e) {
          // A print file is an EXTRA deliverable. Losing one must never fail the mockup that the
          // operator is actually watching — but it must never be silent either.
          opts.onTrace?.({
            itemKey: `${d.designSlug}:print`, phase: "render", startedAt: Date.now(), level: "warn",
            message: `print file not rendered (${e instanceof Error ? e.message : String(e)}) — the uploaded artwork will ship instead`,
          });
        }
      }
    }
  } finally {
    for (const id of Object.values(sessionByView)) {
      try {
        await engine.closeMockupSession?.(id);
      } catch (e) {
        // Teardown must never throw into the run — but it must never be silent either.
        opts.onTrace?.({
          itemKey: `session:${id}`, phase: "preparing", startedAt: Date.now(), level: "warn",
          message: `could not close render session ${id} (${e instanceof Error ? e.message : String(e)}) — it will expire from the native LRU`,
        });
      }
    }
  }

  // WS-O: the outputs were built INSIDE the loop (each one announced the instant its bytes
  // existed), so there is no post-loop map left to run — one construction, one path, one truth.
  const outputs: OutputRecord[] = result.outputs;

  // WS-H2 / D-10: these are the RENDERED print files — the back print area re-rendered at
  // `render_settings.print_dpi` by the engine, with real bytes and a real dpi. Empty on web and
  // on any template that is not a print-file source, in which case `printFilesFromOutputs` still
  // ships the customer's uploaded artwork exactly as before. Their PATHS are derived at export
  // time by that same function, so a rendered and a fallback print file land identically.
  const printFiles = renderedPrintFiles;

  return { batchId: batch.id, outputs, printFiles, failures: result.failures, aborted };
}

/** The filename's leaf (drop any leading folder). The EXTENSION is dropped downstream by
 *  `designFileBase`, which removes image extensions only — this used to strip any trailing
 *  `.token` itself, so an extension-less "logo.v1" lost its version before dedup even ran. */
function baseName(filename: string): string {
  return filename.split(/[\\/]/).pop() ?? filename;
}
